import { DateInput, SearchBarExpansiveCustomField } from '@components/index';
import React, { memo } from 'react';
import { nullIfEmpty } from '@utils/nullIfEmpty';
import { ChildrenT } from '../../helpers/HeadingsCalc';
import {
  BigInputDiv,
  BigStyledInput,
  BigStyledTextArea,
  GlobalHeader,
  InputList,
} from '../styles';
import { PropsInputGlobal } from './types';
import { extractFieldId, onChangeGlobal, setForAllNew } from './utils';
import { UniqueInput } from './UniqueInput';

type CustomFieldGroupProps = PropsInputGlobal<
  'customFields' | `customFields.${string}.content`
>;

export function CustomFieldGroup(props: CustomFieldGroupProps) {
  const { setFormV, activePage, customFieldsQuery, customFields } = props;
  const id = extractFieldId(activePage.id as ChildrenT);
  const postfix = `customFields.${id}.content` as const;
  const { [postfix]: value, setGlobalFormV } = props;
  const name = `equipment.${postfix}` as const;

  const fieldKeys = Object.keys(customFields);
  const customFieldMeta = customFieldsQuery.find(({ id: _id }) => _id === id);
  let options: string[] = [];

  if (customFieldMeta && customFieldMeta.selectableValues) {
    options = customFieldMeta.selectableValues;
  }
  const { isUnique = false } = customFieldMeta || {};
  const distinctInputs = fieldKeys.map(key => {
    // This helps to remount the controller between pages by letting react know something "Changed".
    if (key === id)
      return <React.Fragment key={key}>{getInput()}</React.Fragment>;
    return <React.Fragment key={key} />;
  });

  return (
    <InputList>
      <BigInputDiv>
        <GlobalHeader>{activePage.name}</GlobalHeader>
        {distinctInputs}
      </BigInputDiv>
    </InputList>
  );
  function getInput() {
    if (isUnique) return <UniqueInput />;

    switch (activePage.inputType) {
      // Placing the controller inside the function is necessary because a shared one will bleed values across same input type.
      case 'custom-type-date': {
        let valueOrNull = null;
        if (value !== null && value !== undefined)
          valueOrNull = new Date(value);
        return (
          <DateInput
            name={name}
            value={valueOrNull}
            placeholder={activePage.name}
            handleDateChange={e => {
              const convDate: string | null = e ?? null;
              onChangeGlobal(postfix, convDate, setGlobalFormV);
              setForAllNew(postfix, convDate, setFormV);
            }}
          />
        );
      }
      case 'custom-type-contact':
      case 'custom-type-email':
      case 'custom-type-url':
      case 'custom-type-text':
        return (
          <BigStyledInput
            type="text"
            name={name}
            value={value ?? ''}
            placeholder={activePage.name}
            onChange={e => {
              const cleanValue = nullIfEmpty.setValueAs(e.target.value);
              onChangeGlobal(postfix, cleanValue, setGlobalFormV);

              setForAllNew(postfix, cleanValue, setFormV);
            }}
          />
        );

      case 'custom-type-phone':
        return (
          <BigStyledInput
            type="number"
            name={name}
            value={value ?? ''}
            placeholder={activePage.name}
            onChange={e => {
              const cleanValue = nullIfEmpty.setValueAs(e.target.value);
              onChangeGlobal(postfix, cleanValue, setGlobalFormV);

              setForAllNew(postfix, cleanValue, setFormV);
            }}
          />
        );
      case 'custom-type-select':
        return (
          <SearchBarExpansiveCustomField
            options={options}
            selectedValue={value ?? null}
            placeholder={activePage.name}
            onCloseIconClick={() => {
              if (value) {
                onChangeGlobal(postfix, null, setGlobalFormV);
                setForAllNew(postfix, null, setFormV);
              }
            }}
            onSelect={input => {
              onChangeGlobal(postfix, input, setGlobalFormV);
              setForAllNew(postfix, input, setFormV);
            }}
          />
        );
      case 'custom-type-description': {
        return (
          <BigStyledTextArea
            name={name}
            placeholder={activePage.name}
            value={value ?? ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const cleanValue = nullIfEmpty.setValueAs(e.target.value);
              onChangeGlobal(postfix, cleanValue, setGlobalFormV);

              setForAllNew(postfix, cleanValue, setFormV);
            }}
            className="resize-x"
          />
        );
      }
      default:
        return <></>;
    }
  }
}
export default memo(CustomFieldGroup);
